import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, KeyRound, Shield, User as UserIcon, X, Search, RefreshCw, Users, Link2 } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { listUsers, createUser, deleteUser, resetPassword } from '../api/admin';
import { apiClient } from '../api/client';
import type { User } from '../types';

export const AdminPage: React.FC = () => {
  const { user } = useAuthStore();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showReset, setShowReset] = useState<number | null>(null);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState<'admin' | 'user'>('user');
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [workingId, setWorkingId] = useState<number | null>(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await listUsers();
      setUsers(data);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionError('');
    setActionSuccess('');
    try {
      await createUser({ username: newUsername, password: newPassword, role: newRole });
      setActionSuccess(`User "${newUsername}" created.`);
      setNewUsername('');
      setNewPassword('');
      setNewRole('user');
      setShowCreate(false);
      await fetchUsers();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to create user');
    }
  };

  const handleDelete = async (id: number, username: string) => {
    if (!window.confirm(`Delete user "${username}"? All progress will be removed.`)) return;
    setActionError('');
    setActionSuccess('');
    setWorkingId(id);
    try {
      await deleteUser(id);
      setActionSuccess(`User "${username}" deleted.`);
      await fetchUsers();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setWorkingId(null);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!showReset) return;
    setActionError('');
    setActionSuccess('');
    setWorkingId(showReset);
    try {
      await resetPassword(showReset, { new_password: resetPasswordValue });
      setActionSuccess('Password reset successfully.');
      setResetPasswordValue('');
      setShowReset(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setWorkingId(null);
    }
  };

  const handleResetLink = async (id: number, username: string) => {
    setActionError('');
    setActionSuccess('');
    setWorkingId(id);
    try {
      const { data } = await apiClient.post<{ reset_url: string; expires_in_minutes: number }>(
        `/admin/users/${id}/reset-link`
      );
      try {
        await navigator.clipboard.writeText(data.reset_url);
        setActionSuccess(`Reset link for "${username}" copied to clipboard (valid ${data.expires_in_minutes} min).`);
      } catch {
        setActionSuccess(`Reset link for "${username}" (valid ${data.expires_in_minutes} min): ${data.reset_url}`);
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to create reset link');
    } finally {
      setWorkingId(null);
    }
  };

  const filteredUsers = users.filter((u) =>
    u.username.toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  const totalUsers = users.length;
  const adminCount = users.filter((u) => u.role === 'admin').length;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 pb-24 md:pb-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-bold text-cream flex items-center gap-2">
            <Shield size={20} className="text-gold" />
            Admin Panel
          </h1>
          <p className="text-xs text-text-dim mt-1">
            Signed in as {user?.username} ({user?.role})
          </p>
        </div>
        <button
          onClick={() => { setLoading(true); fetchUsers(); }}
          className="px-3 py-1.5 text-xs rounded-lg border border-border bg-surface text-text-dim hover:text-cream hover:bg-surface-1 flex items-center gap-1.5 transition-colors"
        >
          <RefreshCw size={12} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
        <StatCard icon={Users} label="Total Users" value={totalUsers} />
        <StatCard icon={Shield} label="Admins" value={adminCount} />
        <StatCard icon={UserIcon} label="Regular" value={totalUsers - adminCount} />
        <StatCard icon={Plus} label="New (7d)" value={users.filter((u) => u.created_at && new Date(u.created_at) > new Date(Date.now() - 7 * 86400000)).length} />
      </div>

      {/* Alerts */}
      <AnimatePresence>
        {(actionError || actionSuccess) && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium ${
              actionError ? 'bg-red-muted text-red border border-red/20' : 'bg-green-muted text-green border border-green/20'
            }`}
          >
            {actionError || actionSuccess}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create user form */}
      <div className="mb-6">
        {!showCreate ? (
          <button
            onClick={() => { setShowCreate(true); setActionError(''); setActionSuccess(''); }}
            className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gold-glow border border-gold-dim text-gold text-sm font-medium hover:bg-gold-dim transition-colors"
          >
            <Plus size={16} />
            Add User
          </button>
        ) : (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="glass-strong rounded-2xl p-5 border border-border"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-cream">Create New User</h2>
              <button onClick={() => setShowCreate(false)} className="text-text-dim hover:text-cream">
                <X size={16} />
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input
                  type="text"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  required
                  minLength={2}
                  placeholder="Username"
                  className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-cream outline-none focus:border-gold placeholder:text-text-dim"
                />
                <div className="relative">
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    placeholder="Password"
                    className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-cream outline-none focus:border-gold placeholder:text-text-dim"
                  />
                </div>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value as 'admin' | 'user')}
                  className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-cream outline-none focus:border-gold"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex justify-end">
                <button type="submit" className="px-5 py-2 rounded-lg btn-gold text-sm font-semibold">
                  Create User
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
        <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search users..."
          className="w-full bg-white/[0.04] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-cream outline-none focus:border-gold placeholder:text-text-dim"
        />
      </div>

      {/* User list */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <span className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="text-center py-16 text-red text-sm">{error}</div>
      ) : (
        <div className="space-y-2">
          {filteredUsers.map((u) => (
            <div
              key={u.id}
              className="flex items-center justify-between px-4 py-3 rounded-xl glass border border-border"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                  u.role === 'admin' ? 'bg-gold-glow text-gold' : 'bg-surface text-text-dim'
                }`}>
                  {u.role === 'admin' ? <Shield size={14} /> : <UserIcon size={14} />}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-cream truncate">{u.username}</p>
                  <p className="text-[11px] text-text-dim capitalize">
                    {u.role}
                    {u.created_at && ` • ${new Date(u.created_at).toLocaleDateString()}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {u.id !== user?.id && (
                  <>
                    <button
                      onClick={() => {
                        setShowReset(showReset === u.id ? null : u.id);
                        setResetPasswordValue('');
                        setActionError('');
                        setActionSuccess('');
                      }}
                      disabled={workingId === u.id}
                      className="p-2 rounded-lg text-text-dim hover:text-gold hover:bg-gold-glow transition-colors disabled:opacity-40"
                      title="Reset password"
                    >
                      <KeyRound size={15} />
                    </button>
                    <button
                      onClick={() => handleResetLink(u.id, u.username)}
                      disabled={workingId === u.id}
                      className="p-2 rounded-lg text-text-dim hover:text-gold hover:bg-gold-glow transition-colors disabled:opacity-40"
                      title="Copy a one-time password reset link"
                    >
                      <Link2 size={15} />
                    </button>
                    <button
                      onClick={() => handleDelete(u.id, u.username)}
                      disabled={workingId === u.id}
                      className="p-2 rounded-lg text-text-dim hover:text-red hover:bg-red-muted transition-colors disabled:opacity-40"
                      title="Delete user"
                    >
                      <Trash2 size={15} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}

          {showReset && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 glass-strong rounded-xl p-4 border border-border"
            >
              <form onSubmit={handleReset} className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="block text-[11px] font-medium uppercase tracking-wider text-text-dim mb-1.5">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={resetPasswordValue}
                    onChange={(e) => setResetPasswordValue(e.target.value)}
                    required
                    minLength={6}
                    className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-cream outline-none focus:border-gold"
                    placeholder="Enter new password"
                  />
                </div>
                <button type="submit" disabled={workingId === showReset} className="px-4 py-2 rounded-lg btn-gold text-sm font-semibold disabled:opacity-40">
                  Reset
                </button>
                <button
                  type="button"
                  onClick={() => setShowReset(null)}
                  className="px-4 py-2 rounded-lg border border-border text-text-dim text-sm hover:text-cream transition-colors"
                >
                  Cancel
                </button>
              </form>
            </motion.div>
          )}

          {filteredUsers.length === 0 && (
            <p className="text-center text-sm text-text-dim py-10">No users found.</p>
          )}
        </div>
      )}
    </div>
  );
};

const StatCard: React.FC<{ icon: React.ElementType; label: string; value: number }> = ({ icon: Icon, label, value }) => (
  <div className="glass border border-border rounded-xl p-3">
    <div className="flex items-center gap-2 text-text-dim text-xs mb-1">
      <Icon size={13} />
      <span>{label}</span>
    </div>
    <div className="text-xl font-bold text-cream">{value}</div>
  </div>
);
